// @flow
import * as React from 'react';

import assertions from 'lib/assertions';
import Button from 'components/Button.jsx';
import Buttons from 'components/Buttons.jsx';
import Container from 'components/Container.jsx';
import FormElement from 'components/FormElement.jsx';
import Heading from 'components/Heading.jsx';
import Input from 'components/Input.jsx';
import Link from 'components/Link.jsx';
import Row from 'components/Row.jsx';
import Select from 'components/Select.jsx';
import Text from 'components/Text.jsx';
import TextWithGlyph from 'components/TextWithGlyph.jsx';
import View from 'components/View.jsx';
import WizardFooter from 'components/WizardFooter.jsx';

import type { AutoMessageOrg } from './';

type Props = {
  orgs: ?(AutoMessageOrg[]),
  messageName: ?string,
  orgUuid: ?string,
  onCreate: (messageName: ?string, orgUuid: ?string) => void,
};

type State = {
  messageNameFromField: ?string,
  orgUuidFromField: ?string,
};

class AdminNewAutoMessageCreate extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      messageNameFromField: props.messageName,
      orgUuidFromField: props.orgUuid,
    };
  }

  componentDidMount() {
    if (this.props.orgs) {
      this.handleOrgChange(this.props.orgs[0].uuid);
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (!prevProps.orgs && this.props.orgs) {
      this.handleOrgChange(this.props.orgs[0].uuid);
    }
  }

  handleOrgChange = (orgUuid: ?string) => {
    this.setState({ orgUuidFromField: orgUuid });
  };

  handleMessageNameChange = (messageName: ?string) => {
    this.setState({ messageNameFromField: messageName });
  };

  render() {
    const { orgs } = this.props;
    const { messageNameFromField, orgUuidFromField } = this.state;
    const orgOptions = orgs
      ? orgs.map(org => {
          return { label: org.name, value: org.uuid };
        })
      : [];

    const selectedOrg = orgs ? orgs.find(org => org.uuid === orgUuidFromField) : null;

    return (
      <React.Fragment>
        <View>
          <Row size="xlarge">
            <Container flush size="medium">
              <Text align="left" lineHeight="large">
                <Heading level="1">Create a new auto message draft</Heading>
                <Row size="large">
                  <Text element="div">
                    Automatically send messages to students and parents based on data from your SIS
                    or other database. You’ll be walked through the process of exporting reports
                    with recipient data, transfering this data to Remind, composing your message,
                    and activating your auto message.
                  </Text>
                </Row>
                <Container size="small" flush align="left">
                  <Row size="large">
                    <FormElement
                      showErrorBeforeBlur
                      label="Message name"
                      onValidChange={e => this.handleMessageNameChange(e.target.value)}
                      onInvalidChange={() => this.handleMessageNameChange(undefined)}
                      subtext="Give this auto message a name that is easily distinguishable. Message names are only
                visible to you and other administrators."
                      validate={e => {
                        assertions.lengthLesserThanOrEqualTo(
                          e.target.value,
                          25,
                          'Please provide a message name under 25 characters long.'
                        );
                      }}
                      element={Input}
                      elementProps={{
                        defaultValue: messageNameFromField,
                        placeholder:
                          '1st Period Attendance, Weekly Lunch Money Balance, Overdue Library Books',
                        name: 'messageName',
                      }}
                    />
                  </Row>
                  <FormElement
                    label="Organization"
                    validate={value => assertions.notEmpty(value, 'Please select an organization')}
                    onValidChange={value => this.handleOrgChange(value)}
                    onInvalidChange={() => this.handleOrgChange(undefined)}
                    subtext="Recipients must be a member of this organization to receive this auto message."
                    element={Select}
                    elementProps={{
                      value: orgUuidFromField,
                      placeholder: 'Choose a school or district',
                      name: 'org',
                      options: orgOptions,
                    }}
                  />
                </Container>
                {selectedOrg && selectedOrg.studentIdPercentage < 0.5 ? (
                  <TextWithGlyph center={false} glyph="caution" glyphColor="sunrise" size="xsmall">
                    {`${selectedOrg.studentIdPercentage * 100}% of students at ${
                      selectedOrg.name
                    } have student IDs on Remind. You’ll only be able to message students with student IDs. `}
                    <Link href="#">Learn more here</Link>
                  </TextWithGlyph>
                ) : null}
              </Text>
            </Container>
          </Row>
        </View>
        <WizardFooter>
          <Container size="medium" flush>
            <Buttons align="right">
              <Button
                data-test="continue-button"
                disabled={!messageNameFromField || !orgUuidFromField}
                onClick={() => this.props.onCreate(messageNameFromField, orgUuidFromField)}>
                Create draft
              </Button>
            </Buttons>
          </Container>
        </WizardFooter>
      </React.Fragment>
    );
  }
}

export default AdminNewAutoMessageCreate;
